import React, {ReactNode} from 'react';
import Box from "@mui/material/Box";
import Typography, {TypographyProps} from "@mui/material/Typography";
import Button from "@mui/material/Button";
import MuiMenu, {MenuProps} from "@mui/material/Menu";
import {
    NotificacionTipos,
    NotificacionVistaDTO,
    NotificacionVistaFields
} from "../../../../../pages/notifications/api/schemas";
import useMediaQuery from "@mui/material/useMediaQuery";
import {styled, Theme} from "@mui/material/styles";
import MuiMenuItem, {MenuItemProps} from "@mui/material/MenuItem";
import PerfectScrollbarComponent from "react-perfect-scrollbar";
import CustomAvatar from "../../../../components/mui/avatar";
import {CustomAvatarProps} from "../../../../components/mui/avatar/types";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {Tooltip} from "@mui/material";
import Icon from "../../../../components/icon";
import {rutaPorCodSistema} from "../../../../../pages/sistema/RefactorLayout";

// ** Styled Menu component
const Menu = styled(MuiMenu)<MenuProps>(({theme}) => ({
    '& .MuiMenu-paper': {
        width: 380,

        // width: 500,
        overflow: 'hidden',
        marginTop: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            width: '100%'
        }
    },
    '& .MuiMenu-list': {
        padding: 0
    }
}))

// ** Styled MenuItem component
const MenuItem = styled(MuiMenuItem)<MenuItemProps>(({theme}) => ({
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    '&:not(:last-of-type)': {
        borderBottom: `1px solid ${theme.palette.divider}`
    }
}))

// ** Styled PerfectScrollbar component
const PerfectScrollbar = styled(PerfectScrollbarComponent)({
    maxHeight: 349
})

// ** Styled Avatar component
const Avatar = styled(CustomAvatar)<CustomAvatarProps>({
    width: 38,
    height: 38,
    fontSize: '1.125rem'
})

// ** Styled component for the title in MenuItems
const MenuItemTitle = styled(Typography)<TypographyProps>(({theme}) => ({
    fontWeight: 600,
    flex: '1 1 100%',
    overflow: 'hidden',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    marginBottom: theme.spacing(0.75)
}))

// ** Styled component for the subtitle in MenuItems
const MenuItemSubtitle = styled(Typography)<TypographyProps>({
    flex: '1 1 100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
})

export const RenderAvatar = ({notification}: { notification: NotificacionVistaDTO }) => {
    const mapaTipoNotificacionIcono = {
        [NotificacionTipos.NUEVO]: 'fluent:new-16-regular',
        [NotificacionTipos.ATRASADO]: 'mdi:hourglass',
    }

    return (
        <Avatar skin='light' color={'primary'}>
            {/*{getInitials(notification[NotificacionVistaFields.Tipo] as string)}*/}
            {/*✒*/}
            <Icon icon={mapaTipoNotificacionIcono[notification[NotificacionVistaFields.Tipo]]}/>
        </Avatar>
    )
}

interface NotificationItemProps {
    notification: NotificacionVistaDTO,
    alertIcon?: boolean
}

export function NotificationItem({notification, alertIcon}: NotificationItemProps) {
    return (
        <Box sx={{width: '100%', display: 'flex', alignItems: 'center', gap: 2}}>
            <RenderAvatar notification={notification}/>
            <Box
                sx={{mx: 0, flex: '1 1', display: 'flex', overflow: 'hidden', flexDirection: 'column'}}>
                <MenuItemTitle>{`${notification[NotificacionVistaFields.Tipo]} (#${notification[NotificacionVistaFields.IdMensaje]})`}</MenuItemTitle>
                <Tooltip title={notification[NotificacionVistaFields.Mensaje]}>
                    <MenuItemSubtitle
                        variant='body2'>{notification[NotificacionVistaFields.Mensaje]}</MenuItemSubtitle>
                </Tooltip>

            </Box>
            <Typography variant='caption' sx={{color: 'text.disabled'}}>
                {(notification[NotificacionVistaFields.Prioridad] && alertIcon) &&
                    <WarningAmberIcon sx={{
                        fill: (theme) => theme.palette.error.main,
                        fontSize: "19px !important",
                    }}/>}
            </Typography>
        </Box>
    )
}

const ScrollWrapper = ({children, hidden}: { children: ReactNode; hidden: boolean }) => {
    if (hidden) {
        return <Box sx={{maxHeight: 349, overflowY: 'auto', overflowX: 'hidden'}}>{children}</Box>
    } else {
        return <PerfectScrollbar
            options={{wheelPropagation: false, suppressScrollX: true}}>{children}</PerfectScrollbar>
    }
}

interface NotificacionMenuProps extends MenuProps {
    notificaciones: NotificacionVistaDTO[],
    alertIcon?: boolean,
}

function NotificacionMenu({notificaciones, alertIcon, ...menuProps}: NotificacionMenuProps) {
    const navigate = useNavigate()

    // ** Translation
    const {t} = useTranslation()

    // ** Hook
    const hidden = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))

    const handleGoToNotifications = () => {
        navigate('/notifications')
    }

    return (
        <Menu

            {...menuProps}
        >
            <MenuItem
                disableRipple
                disableTouchRipple
                sx={{cursor: 'default', userSelect: 'auto', backgroundColor: 'transparent !important'}}
            >
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
                    <Typography sx={{cursor: 'text', fontWeight: 600}}>{t('Notificaciones')}</Typography>
                </Box>
            </MenuItem>
            {notificaciones.map((notification: NotificacionVistaDTO, index: number) => (
                <MenuItem key={index}
                          onClick={() => navigate(`/operacion/${rutaPorCodSistema[notification[NotificacionVistaFields.CodSistema]]}/${notification[NotificacionVistaFields.IdMensaje]}`)}>
                    <NotificationItem notification={notification} alertIcon={alertIcon}/>
                </MenuItem>
            ))}
            <MenuItem
                disableRipple
                disableTouchRipple
                sx={{
                    py: 3.5,
                    borderBottom: 0,
                    cursor: 'default',
                    userSelect: 'auto',
                    backgroundColor: 'transparent !important',
                    borderTop: theme => `1px solid ${theme.palette.divider}`
                }}
            >
                <Button fullWidth variant='contained' onClick={handleGoToNotifications}>
                    {t('Ver todas')}
                </Button>
            </MenuItem>
        </Menu>
    );
}

export default NotificacionMenu;